import {
  Component,
  OnInit,
  HostListener,
  ElementRef,
  AfterViewInit,
  ChangeDetectorRef,
  DestroyRef,
  ViewChild,
  computed,
  signal,
  effect,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { filter, map, throttleTime } from 'rxjs/operators';
import { forkJoin, timer, merge } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

// PrimeNG 21 Components
import { ButtonModule } from 'primeng/button';
import { Menu, MenuModule } from 'primeng/menu';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { BadgeModule } from 'primeng/badge';
import { ToastModule } from 'primeng/toast';

// Services
import { AuthService } from '../../core/services/auth.service';
import { UserSessionService } from '../../core/services/mock-auth.service';
import { AuthorizationService } from '../../core/services/authorization.service';
import { ProfessionalsDataService } from '../../frontoffice/services/professionals-data.service';
import { ProfessionalsService } from '../../core/services/professionals.service';
import { FormDropdownService } from '../../core/services/form-dropdown.service';
import { NavigationContextService } from '../../core/services/navigation-context.service';
import { Alert } from '../../core/models/models';

@Component({
  selector: 'app-headerbar',
  standalone: true,
  templateUrl: './headerbar.html',
  styleUrls: ['./headerbar.scss'],
  imports: [
    CommonModule,
    RouterModule,
    ButtonModule,
    MenuModule,
    TagModule,
    TooltipModule,
    BadgeModule,
    ToastModule,
  ],
  providers: [MessageService],
})
export class HeaderBarComponent implements OnInit, AfterViewInit {
  private router = inject(Router);
  private elementRef = inject(ElementRef);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private authService = inject(AuthService);
  private mockAuthService = inject(UserSessionService);
  private authzService = inject(AuthorizationService);
  private professionalsDataService = inject(ProfessionalsDataService);
  private professionalsService = inject(ProfessionalsService);
  private formDropdownService = inject(FormDropdownService);
  private messageService = inject(MessageService);
  public navigationContext = inject(NavigationContextService);

  isDark = false;
  activeDropdownLabel: string | null = null;

  private getContractsRegistryRoute(): string {
    return `/axa/${this.navigationContext.getDomainPrefix()}-contracts-registry`;
  }

  userMenuItems: MenuItem[] = [
    {
      separator: true,
    },
    {
      label: 'Logout',
      icon: 'pi pi-sign-out',
      command: () => {
        console.log('🖱️ [Headerbar] Logout menu item clicked');
        this.logout();
      },
    },
  ];
  navItems: { label: string; icon: string; route: string; active: boolean; isPrimary?: boolean; children?: { label: string; icon: string; route: string; command?: () => void }[] }[] = [];
  @ViewChild('navDropdownMenu') navDropdownMenu!: Menu;
  activeNavChildren: MenuItem[] = [];
  currentRole: string | null = null;
  
  // Mobile menu state
  isMobileMenuOpen = signal(false);

  // User profile data (reactive signals)
  userName = computed(() => this.authzService.currentUser()?.name || null);
  
  /**
   * User email with external identity suffix removed
   * External users have emails like: asiacaramel_gmail.com@identity.deloitte
   * This removes the @identity.deloitte suffix to show cleaner emails
   */
  userEmail = computed(() => {
    const email = this.authzService.currentUser()?.email || null;
    return this.cleanExternalUserEmail(email);
  });
  
  professionalId: string | null = null;
  
  // Raw professional type from API
  private rawProfessionalType = signal<string | null>(null);
  private rawTipologiaCarrozzeria = signal<string | null>(null);
  
  // Professional type labels loaded from dropdown service
  private professionalTypeLabels = signal<Record<string, string>>({});
  
  // Computed professional type display with decoded label
  professionalType = computed(() => {
    const rawType = this.rawProfessionalType();
    const tipologiaCarrozzeria = this.rawTipologiaCarrozzeria();
    const labels = this.professionalTypeLabels();
    
    if (!rawType) return null;
    
    let label = labels[rawType] || rawType;
    
    // Se è carrozzeria, aggiungi il sottotipo
    if ((rawType === 'CARROZZERIA' || rawType === 'BODYSHOP') && tipologiaCarrozzeria) {
      label = label ? `${label} ${tipologiaCarrozzeria}` : `Carrozzeria ${tipologiaCarrozzeria}`;
    }
    
    return label;
  });
  
  userRoles = computed(() => this.authzService.currentUser()?.roles || []);

  // Cache management for professional data
  private lastProfileDataLoad: number | null = null;
  private readonly PROFILE_CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes cache

  // Mapping ruoli per visualizzazione user-friendly
  private roleLabels: Map<string, { label: string; severity: string }> = new Map([
    ['admin', { label: '👑 Super Admin', severity: 'danger' }],
    ['backoffice_admin', { label: '💼 Backoffice Admin', severity: 'success' }],
    ['backoffice_professional', { label: '👥 Backoffice Fiduciari', severity: 'info' }],
    ['backoffice_supplier', { label: '🏢 Backoffice Fornitori', severity: 'info' }],
    ['professional', { label: '⚖️ Fiuciario', severity: 'warning' }],
    ['collaborator', { label: '🤝 Collaboratore Fiduciario', severity: 'secondary' }],
  ]);

  // Alerts
  alerts: Alert[] = [];
  alertsLoading = false;
  showAlertsPanel = false;
  unreadAlertsCount = 0;

  // Track URL for login page detection
  private currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  // Computed to check if we are on login page
  isLoginPage = computed(() => {
    const url = this.currentUrl();
    return url?.includes('/login') || url?.includes('/auth-test') || false;
  });

  constructor() {
    // 🔍 Monitor currentUser changes in real-time and rebuild navigation
    effect(() => {
      const user = this.authzService.currentUser();
      console.log('🔍 [Headerbar Effect] currentUser changed:', user);
      console.log('🔍 [Headerbar Effect] userName:', this.userName());
      console.log('🔍 [Headerbar Effect] userEmail:', this.userEmail());
      console.log('🔍 [Headerbar Effect] userRoles:', this.userRoles());
      
      // 🆕 Rebuild navigation menu when user changes (e.g., after login)
      if (user && user.roles && user.roles.length > 0) {
        console.log('🔄 [Headerbar Effect] User loaded with roles, updating navigation menu');
        // Update current role from the signal-based user
        this.currentRole = user.roles[0] || null;
        // Rebuild navigation items based on new role
        this.setNavItemsByRole();
        this.updateActiveTab();
      }
    });
  }

  ngAfterViewInit(): void {
    queueMicrotask(() => this.cdr.detectChanges());
  }

  ngOnInit() {
    // Initialize dark mode from localStorage
    this.initializeDarkMode();

    // Get current user role
    this.updateCurrentRole();

    // All API calls require the token to be available in memory first
    this.authService.initialized.then(() => {
      // Load professional type labels from dropdown service
      this.loadProfessionalTypeLabels();

      // Load professional data immediately if user is professional/collaborator
      const currentUser = this.authzService.currentUser();
      const isProfessional = currentUser?.roles.some(role =>
        role === 'professional' || role === 'collaborator'
      );
      if (isProfessional) {
        console.log('👤 [Headerbar] Professional user detected - loading profile data immediately');
        this.loadUserProfileData();
      }

      // Load alerts only if user is logged in and not on login pages
      if (this.shouldLoadAlerts()) {
        this.loadAlerts();

        // Polling ogni 2 minuti + reload immediato dopo ogni azione su un alert
        merge(
          timer(120_000, 120_000),
          this.professionalsService.alertsChanged$.pipe(throttleTime(500)),
        )
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            if (this.shouldLoadAlerts()) {
              this.loadAlerts(true); // silent: no loading spinner
            }
          });
      }
    });

    // Listen to route changes (no token needed - only UI updates)
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      // Re-check navigation items in case professional ID was created during pre-registration
      this.setNavItemsByRole();
      // THEN update active state (must be after setNavItemsByRole to avoid reset)
      this.updateActiveTab();

      // Only reload professional data if coming from pre-registration page
      // This avoids unnecessary API calls on every navigation
      const comingFromPreRegistration = event.url.includes('/questionnaires') ||
                                        event.url.includes('/area-personale');
      if (comingFromPreRegistration) {
        console.log('📍 [Headerbar] Coming from pre-registration area, reloading profile data');
        this.loadUserProfileData(true); // Force reload
      }
    });
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen.update(value => !value);
  }
  
  closeMobileMenu() {
    this.isMobileMenuOpen.set(false);
  }
  
  loadProfessionalTypeLabels() {
    // ✅ Check if user is authenticated before making API call
    if (!this.authService.isAuthenticated()) {
      console.log('⚠️ [Headerbar] No authentication token, skip loadProfessionalTypeLabels API call');
      return;
    }

    this.formDropdownService
      .getFormDropdownValues('TRUSTEE_CATEGORY', 'TRUSTEE_CATEGORY')
      .subscribe({
        next: (response) => {
          if (response?.status && Array.isArray(response?.data)) {
            const labelsMap: Record<string, string> = {};
            response.data.forEach((item) => {
              labelsMap[item.valueKey] = item.valueDescription;
            });
            this.professionalTypeLabels.set(labelsMap);
            console.log('✅ [Headerbar] Professional type labels loaded:', labelsMap);
          }
        },
        error: (err) => {
          console.error('❌ [Headerbar] Error loading professional type labels:', err);
        },
      });
  }

  loadUserProfileData(forceReload: boolean = false) {
    // Check cache validity (skip if data was loaded recently, unless forced)
    if (!forceReload && this.isProfileCacheValid()) {
      const timeSinceLastLoad = Date.now() - this.lastProfileDataLoad!;
      console.log(`⚡ [Headerbar] Using cached profile data (${Math.round(timeSinceLastLoad / 1000)}s ago)`);
      return;
    }

    // userName, userEmail, and userRoles are now computed signals - no need to set them
    // Just load professionalId and professionalType

    // 🔍 Debug: Log current user data from authzService
    const currentUser = this.authzService.currentUser();
    console.log('🔍 [Headerbar] Current user from authzService:', currentUser);
    console.log('🔍 [Headerbar] userName:', this.userName());
    console.log('🔍 [Headerbar] userEmail:', this.userEmail());
    console.log('🔍 [Headerbar] userRoles:', this.userRoles());

    const professionalId = this.mockAuthService.getDemoProfessionalId();
    this.professionalId = professionalId;

    // ✅ Check if user is authenticated before making API call
    const hasToken = !!localStorage.getItem('access_token');
    if (!hasToken) {
      console.log('⚠️ [Headerbar] No access token found, skip getProfessionalGeneralData API call');
      return;
    }

    // Chiamata API per recuperare i dati generali del professional, incluso professionalType
    if (professionalId) {
      console.log(`🔄 [Headerbar] Loading professional data (forceReload: ${forceReload})`);
      this.professionalsService.getProfessionalGeneralData(professionalId).subscribe({
        next: (response) => {
          // Update cache timestamp
          this.lastProfileDataLoad = Date.now();
          
          if (response?.data?.professionalType) {
            const professionalType = response.data.professionalType;
            const tipologiaCarrozzeria = response.data.tipologiaCarrozzeria;

            // Update signals - computed will handle label decoding
            this.rawProfessionalType.set(professionalType);
            this.rawTipologiaCarrozzeria.set(tipologiaCarrozzeria || null);
            
            console.log('✅ [Headerbar] Professional type loaded:', professionalType);
          }
        },
        error: (err) => {
          console.error('❌ Error loading professional general data:', err);
          // Fallback al servizio in memoria se l'API fallisce
          this.loadProfessionalTypeFromMemory();
        },
      });
    } else {
      // Nessun professional ID, fallback al servizio in memoria
      this.loadProfessionalTypeFromMemory();
    }
  }

  /**
   * Invalidate profile data cache to force reload on next call
   * Useful when other components know that professional data has changed
   */
  invalidateProfileCache(): void {
    console.log('🔄 [Headerbar] Profile cache invalidated');
    this.lastProfileDataLoad = null;
  }

  /**
   * Check if profile data cache is still valid
   */
  private isProfileCacheValid(): boolean {
    if (!this.lastProfileDataLoad) return false;
    const timeSinceLastLoad = Date.now() - this.lastProfileDataLoad;
    return timeSinceLastLoad < this.PROFILE_CACHE_DURATION_MS;
  }

  private loadProfessionalTypeFromMemory() {
    const tipologia = this.professionalsDataService.getTipologiaFiduciario();
    const tipologiaCarrozzeria = this.professionalsDataService.getTipologiaCarrozzeria();

    if (tipologia) {
      // Update signals - computed will handle label decoding
      this.rawProfessionalType.set(tipologia);
      this.rawTipologiaCarrozzeria.set(tipologiaCarrozzeria || null);
      
      console.log('✅ [Headerbar] Professional type loaded from memory:', tipologia);
    }
  }

  updateActiveTab() {
    const currentRoute = this.router.url;
    console.log('🔄 [Headerbar] updateActiveTab called for route:', currentRoute);
    
    this.navItems.forEach((item) => {
      // Reset active state
      item.active = false;

      // Items con dropdown: attiva il genitore se una voce figlia corrisponde alla route corrente
      if (item.children?.length) {
        item.active = item.children.some(child =>
          child.route && (currentRoute === child.route || currentRoute.startsWith(child.route + '/'))
        );
        return;
      }

      // Dashboard - check for both professionals and suppliers dashboard routes
      if (item.route === '/axa/professionals-dashboard' || item.route === '/axa/suppliers-dashboard') {
        item.active =
          currentRoute === '/axa/professionals-dashboard' ||
          currentRoute === '/axa/suppliers-dashboard' ||
          currentRoute === '/axa/dashboard' ||
          currentRoute.startsWith('/axa/professionals-dashboard/') ||
          currentRoute.startsWith('/axa/suppliers-dashboard/');
      }
      // Registro tab (both suppliers and professionals registries)
      else if (
        item.route === '/axa/suppliers-registry' ||
        item.route === '/axa/professionals-registry'
      ) {
        item.active =
          currentRoute.includes('/axa/suppliers-registry') ||
          currentRoute.includes('/axa/professionals-registry') ||
          currentRoute.includes('/axa/responsibles-registry') ||
          currentRoute.includes('/axa/sub-responsibles-registry') ||
          currentRoute.includes('/axa/supplier/') ||
          currentRoute.includes('/axa/professional/') ||
          currentRoute.includes('/axa/responsible/') ||
          currentRoute.includes('/axa/sub-responsible/') ||
          currentRoute.includes('/axa/supplier-registration-wizard') ||
          currentRoute.includes('/axa/responsible-registration-wizard') ||
          currentRoute.includes('/axa/sub-responsible-registration-wizard') ||
          currentRoute.includes('/axa/responsible-form') ||
          currentRoute.includes('/axa/sub-responsible-form');
      }
      // Contratti tab - check for all contract-related routes
      else if (item.route === this.getContractsRegistryRoute()) {
        item.active =
          currentRoute.includes('/axa/supplier-contracts-registry') ||
          currentRoute.includes('/axa/professional-contracts-registry') ||
          currentRoute.includes('/axa/supplier-contract-sections') ||
          currentRoute.includes('/axa/professional-contract-sections') ||
          currentRoute.includes('/axa/supplier-contract-registration-wizard') ||
          currentRoute.includes('/axa/professional-contract-registration-wizard') ||
          currentRoute.includes('/axa/contract/');
      }
      // Questionari e Allegati tab - check for all questionnaire-related routes
      else if (item.route === '/questionnaires') {
        item.active =
          currentRoute.includes('/questionnaires') ||
          currentRoute.includes('/axa/questionnaires-and-attachments') ||
          currentRoute.includes('/create-questionnaire') ||
          currentRoute.includes('/questionnaire/');
      }
      // Area Personale Fiduciario - check for professionals area routes
      else if (item.route === '/axa/professionals-registration-progress') {
        item.active =
          currentRoute.includes('/axa/professionals-registration-progress') ||
          currentRoute.includes('/axa/professionals-personal-info') ||
          currentRoute.includes('/axa/professionals-general-data') ||
          currentRoute.includes('/axa/professionals-specific-data') ||
          currentRoute.includes('/axa/professionals-staff-collaborators') ||
          currentRoute.includes('/axa/professionals-administrative-staff') ||
          currentRoute.includes('/axa/professionals-collaborators') ||
          currentRoute.includes('/axa/professionals-contracts') ||
          currentRoute.includes('/axa/professionals-mfa') ||
          currentRoute.includes('/axa/professionals-questionnaire-attachments') ||
          currentRoute.includes('/axa/vrf-questionnaire') ||
          currentRoute.includes('/area-personale');
      }
      // Default behavior for other tabs
      else if (item.route) {
        item.active = currentRoute.includes(item.route);
      }
    });
    
    // Debug: log active tabs
    const activeTabs = this.navItems.filter(item => item.active).map(item => item.label);
    console.log('✅ [Headerbar] Active tabs:', activeTabs.length > 0 ? activeTabs : 'NONE');
  }

  navigateTo(route: string) {
    this.router.navigate([route]);
  }

  openDropdown(label: string): void {
    this.activeDropdownLabel = label;
  }

  closeDropdown(): void {
    this.activeDropdownLabel = null;
  }

  isChildRouteActive(route: string): boolean {
    if (!route) return false;
    const current = this.router.url;
    return current === route || current.startsWith(route + '/');
  }

  openNavDropdown(event: Event, item: { children?: { label: string; icon: string; route: string; command?: () => void }[] }): void {
    this.activeNavChildren = (item.children ?? []).map(child => ({
      label: child.label,
      icon: child.icon,
      command: child.command ?? (() => this.navigateTo(child.route)),
    }));
    this.navDropdownMenu.toggle(event);
  }

  initializeDarkMode() {
    // Check localStorage for saved preference
    const savedTheme = localStorage.getItem('axa-theme');
    if (savedTheme === 'dark') {
      this.isDark = true;
      document.documentElement.classList.add('axa-dark-mode');
    } else {
      this.isDark = false;
      document.documentElement.classList.remove('axa-dark-mode');
    }
  }

  toggleDarkMode() {
    this.isDark = !this.isDark;
    const html = document.documentElement;

    if (this.isDark) {
      html.classList.add('axa-dark-mode');
      localStorage.setItem('axa-theme', 'dark');
    } else {
      html.classList.remove('axa-dark-mode');
      localStorage.setItem('axa-theme', 'light');
    }
  }

  // NOTE: load*Role() methods have been removed for security reasons
  // Use /auth-test page for mock authentication during development

  updateCurrentRole(forceReload: boolean = false) {
    const user = this.mockAuthService.getCurrentUser();
    // AppUser has roles array, take the first role as current for backward compatibility
    this.currentRole = user?.roles?.[0] || null;
    this.loadUserProfileData(forceReload);
    this.setNavItemsByRole();
    this.updateActiveTab();
  }

  private setNavItemsByRole() {
    // Hide navigation on unauthorized page - only show dark mode and user menu
    if (this.router.url.includes('/unauthorized')) {
      this.navItems = [];
      return;
    }

    // Professional, collaborator, and expert users only see Personal Area
    if (
      this.currentRole === 'professional' ||
      this.currentRole === 'collaborator' ||
      this.currentRole === 'expert'
    ) {
      // Only show AREA PERSONALE if professional ID exists (created during pre-registration)
      const professionalId = this.mockAuthService.getDemoProfessionalId();

      if (professionalId) {
        this.navItems = [
          {
            label: 'Area Personale Fiduciario',
            icon: 'pi pi-briefcase',
            route: '/axa/professionals-registration-progress',
            active: false,
            isPrimary: true,
          },
        ];
      } else {
        // No professional ID yet - hide navigation until pre-registration is complete
        this.navItems = [];
      }
      return;
    }

    // Backoffice Supplier - only suppliers and contracts, NO questionnaires
    if (this.currentRole === 'backoffice_supplier') {
      // Alcuni utenti supplier hanno accesso alla dashboard fiduciari (override per email)
      const supplierDashboardRoute = this.authService.getDefaultRouteForUser();
      const isOverriddenToFiduciari = supplierDashboardRoute === '/axa/professionals-dashboard';
      this.navItems = [
        {
          label: 'Dashboard',
          icon: 'pi pi-home',
          route: supplierDashboardRoute,
          active: false,
          isPrimary: true,
        },
        ...(!isOverriddenToFiduciari ? [{
          label: 'Registro Fornitori',
          icon: 'pi pi-users',
          route: '/axa/suppliers-registry',
          active: false,
          isPrimary: true,
        },
        {
          label: 'Contratti Fornitori',
          icon: 'pi pi-file',
          route: '/axa/supplier-contracts-registry',
          active: false,
          isPrimary: true,
        }] : [{
          label: 'Registro Fiduciari',
          icon: 'pi pi-users',
          route: '/axa/professionals-registry',
          active: false,
          isPrimary: true,
        }]),
      ];
      return;
    }

    // Backoffice Professionals - includes Registro Fiduciari + Questionari + Avvisi
    if (this.currentRole === 'backoffice_professional') {
      this.navItems = [
        {
          label: 'Dashboard',
          icon: 'pi pi-home',
          route: '/axa/professionals-dashboard',
          active: false,
          isPrimary: true,
        },
        {
          label: 'Registro Fiduciari',
          icon: 'pi pi-users',
          route: '/axa/professionals-registry',
          active: false,
          isPrimary: true,
        },
        {
          label: 'Contratti Fiduciari',
          icon: 'pi pi-file',
          route: '/axa/professional-contracts-registry',
          active: false,
          isPrimary: true,
        },
        {
          label: 'Questionari e Allegati',
          icon: 'pi pi-clipboard',
          route: '/questionnaires',
          active: false,
          isPrimary: true,
        },
      ];
      return;
    }

    // Admin - include Questionari e Allegati only for admin users
    if (this.currentRole === 'admin') {
      const isSupplier = this.navigationContext.isSupplierContext();
      const contextLabel = isSupplier ? 'Fornitori' : 'Fiduciari';
      const dashboardRoute = isSupplier ? '/axa/suppliers-dashboard' : '/axa/professionals-dashboard';
      this.navItems = [
        {
          label: 'Dashboard',
          icon: 'pi pi-home',
          route: dashboardRoute,
          active: false,
          isPrimary: true,
        },
        {
          label: `Registro ${contextLabel}`,
          icon: 'pi pi-users',
          route: '/axa/suppliers-registry',
          active: false,
          isPrimary: true,
        },
        {
          label: `Contratti ${contextLabel}`,
          icon: 'pi pi-file',
          route: this.getContractsRegistryRoute(),
          active: false,
          isPrimary: true,
        },
        {
          label: 'Questionari e Allegati',
          icon: 'pi pi-clipboard',
          route: '/questionnaires',
          active: false,
          isPrimary: true,
        },
      ];
      return;
    }

    // Backoffice Admin - accesso completo con menu a cascata
    if (this.currentRole === 'backoffice_admin') {
      this.navItems = [
        {
          label: 'Dashboard',
          icon: 'pi pi-home',
          route: '',
          active: false,
          isPrimary: true,
          children: [
            { label: 'Dashboard Fiduciari', icon: 'pi pi-users', route: '/axa/professionals-dashboard' },
            { label: 'Dashboard Fornitori', icon: 'pi pi-building', route: '/axa/suppliers-dashboard' },
          ],
        },
        {
          label: 'Registro',
          icon: 'pi pi-list',
          route: '',
          active: false,
          isPrimary: true,
          children: [
            { label: 'Registro Fiduciari', icon: 'pi pi-users', route: '/axa/professionals-registry' },
            { label: 'Registro Fornitori', icon: 'pi pi-building', route: '/axa/suppliers-registry' },
          ],
        },
        {
          label: 'Contratti',
          icon: 'pi pi-file',
          route: '',
          active: false,
          isPrimary: true,
          children: [
            {
              label: 'Contratti Fiduciari',
              icon: 'pi pi-users',
              route: '/axa/professional-contracts-registry',
            },
            {
              label: 'Contratti Fornitori',
              icon: 'pi pi-building',
              route: '/axa/supplier-contracts-registry',
            },
          ],
        },
        {
          label: 'Questionari',
          icon: 'pi pi-clipboard',
          route: '/questionnaires',
          active: false,
          isPrimary: true,
        },
      ];
      return;
    }
  }

  getAppTitle(): string {
    switch (this.currentRole) {
      case 'admin':
        return 'PalEasy - Console Amministratore';
      case 'supplierAdmin':
        return 'PalEasy - Fornitori';
      case 'expertAdmin':
      case 'expert':
        return 'PalEasy - Fiduciari';
      default:
        return 'PalEasy';
    }
  }

  /**
   * Get role badge configuration for display
   */
  getRoleBadge(role: string): { label: string; severity: string } | null {
    return this.roleLabels.get(role) || null;
  }

  /**
   * Check if current user is a professional or collaborator
   */
  isProfessionalOrCollaborator(): boolean {
    const roles = this.userRoles();
    return roles.some((role: string) => role === 'professional' || role === 'collaborator');
  }

  /**
   * Removes @identity.deloitte suffix and restores proper email format
   * 
   * External users authenticated via Azure AD B2C have emails like:
   * - asiacaramel_gmail.com@identity.deloitte
   * - john_doe_company.com@identity.deloitte
   * 
   * This method cleans them to show the proper email:
   * - asiacaramel@gmail.com
   * - john_doe@company.com
   * 
   * @param email - The email address to clean
   * @returns The cleaned email or null if no email provided
   */
  private cleanExternalUserEmail(email: string | null): string | null {
    if (!email) return null;
    // Remove @identity.deloitte suffix and replace last underscore with @
    return email
      .replace(/@identity\.deloitte$/i, '')
      .replace(/_([^_]+)$/, '@$1');
  }

  /**
   * Check if alerts should be loaded
   * Returns true only if:
   * - User is logged in
   * - Not on login pages (/login or /auth-test)
   * - Not a professional or collaborator role
   */
  shouldLoadAlerts(): boolean {
    const user = this.mockAuthService.getCurrentUser() || this.authService.getCurrentUser();
    if (!user) {
      console.log('⏭️  Skipping alerts load: no user logged in');
      return false;
    }

    // Token must be available in memory (initialized.then() guarantees init is done, not that token exists)
    if (!this.authService.isAuthenticated()) {
      console.log('⏭️  Skipping alerts load: no access token in memory');
      return false;
    }

    // Alerts are for backoffice_professional and backoffice_admin
    const isBackoffice = user.roles.some(role =>
      role === 'backoffice_professional' || role === 'backoffice_admin'
    );
    if (!isBackoffice) {
      console.log('⏭️  Skipping alerts load: not a backoffice user');
      return false;
    }

    // Skip on login / gating pages (login, auth-test, unauthorized, session-expired):
    // the user may still hold a valid token + backoffice role on /unauthorized,
    // but the alert bell and its API calls must not appear there.
    const currentUrl = this.router.url;
    const isNonAppPage =
      currentUrl.includes('/login') ||
      currentUrl.includes('/auth-test') ||
      currentUrl.includes('/unauthorized') ||
      currentUrl.includes('/session-expired');
    if (isNonAppPage) {
      console.log('⏭️  Skipping alerts load: on login/gating page');
      return false;
    }

    return true;
  }

  /**
   * Load alerts
   */
  loadAlerts(silent = false): void {
    if (!silent) this.alertsLoading = true;
    console.log('🔔 Loading alerts...');

    const SUBMITTED_STEPS = ['VERIFICATION', 'CONTRACT_SENT', 'CONTRACT_SIGNATURE', 'CONTRACT_COUNTERSIGNATURE'];

    forkJoin({
      alerts: this.professionalsService.getAlerts(false, true),
      professionals: this.professionalsService.getProfessionals(0, 9999, undefined, { step: SUBMITTED_STEPS }, true),
    }).subscribe({
      next: ({ alerts, professionals }) => {
        const submittedIds = new Set(
          (professionals?.data?.content ?? []).map((p: any) => p.id as string)
        );
        const filtered = alerts.filter(a => submittedIds.has(a.professionalId));
        console.log('✅ Alerts loaded:', filtered);
        this.alerts = filtered;
        this.unreadAlertsCount = filtered.filter((a) => !a.resolved && !a.disabled).length;
        queueMicrotask(() => {
          this.alertsLoading = false;
          this.cdr.markForCheck();
        });
      },
      error: (err) => {
        console.error('❌ Error loading alerts:', err);
        queueMicrotask(() => {
          this.alertsLoading = false;
          this.alerts = [];
          this.unreadAlertsCount = 0;
          this.cdr.markForCheck();
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Impossibile caricare gli avvisi',
          });
        });
      },
    });
  }

  /**
   * Disable an alert (mark as read)
   */
  disableAlert(alertId: string): void {
    this.professionalsService.disableAlert(alertId).subscribe({
      next: () => {
        const alert = this.alerts.find((a) => a.id === alertId);
        if (alert) {
          alert.disabled = true;
          this.unreadAlertsCount = this.alerts.filter((a) => !a.disabled && !a.resolved).length;
        }
        this.messageService.add({
          severity: 'success',
          summary: 'Successo',
          detail: 'Avviso disabilitato',
        });
      },
      error: (err) => {
        console.error('❌ Error disabling alert:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: "Errore nel disabilitare l'avviso",
        });
      },
    });
  }

  /**
   * Delete an alert
   */
  deleteAlert(alertId: string): void {
    this.professionalsService.deleteAlert(alertId).subscribe({
      next: () => {
        this.alerts = this.alerts.filter((a) => a.id !== alertId);
        this.unreadAlertsCount = this.alerts.filter((a) => !a.disabled && !a.resolved).length;
        this.messageService.add({
          severity: 'success',
          summary: 'Successo',
          detail: 'Avviso eliminato',
        });
      },
      error: (err) => {
        console.error('❌ Error deleting alert:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: "Errore nell'eliminare l'avviso",
        });
      },
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.showAlertsPanel && !this.elementRef.nativeElement.contains(event.target)) {
      this.showAlertsPanel = false;
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: Event): void {
    if (this.showAlertsPanel) {
      this.showAlertsPanel = false;
      event.preventDefault();
      event.stopPropagation();
    }
  }

  /**
   * Toggle alerts panel visibility
   */
  toggleAlertsPanel(): void {
    const willOpen = !this.showAlertsPanel;
    this.showAlertsPanel = willOpen;

    if (willOpen && this.shouldLoadAlerts()) {
      this.loadAlerts();
    }
  }

  /**
   * Navigate to questionnaire run view
   */
  navigateToQuestionnaire(questionnaireId: string): void {
    if (questionnaireId) {
      this.showAlertsPanel = false;
      this.router.navigate([`/axa/questionnaires/${questionnaireId}/run`]);
    }
  }

  onAlertKeydown(event: Event, questionnaireId: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.navigateToQuestionnaire(questionnaireId);
  }

  /**
   * Get unread alerts count
   * @deprecated use unreadAlertsCount property instead
   */
  getUnreadAlertsCount(): number {
    return this.unreadAlertsCount;
  }

  /**
   * Get user-friendly role label
   */
  getRoleLabel(role: string): string {
    return this.roleLabels.get(role)?.label || role;
  }

  /**
   * Get role severity for tag styling
   */
  getRoleSeverity(role: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    const severity = this.roleLabels.get(role)?.severity || 'secondary';
    return severity as 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast';
  }

  /**
   * Logout - pulisce tutti i dati di autenticazione (OAuth + Mock)
   * Reindirizza automaticamente alla pagina di login
   */
  logout(): void {
    console.log('👋 [Headerbar] Triggering logout...');
    console.log('🔍 [Headerbar] Current route before logout:', this.router.url);

    // 1. Pulisci dati mock (se presenti)
    this.mockAuthService.clearAuth();

    // 2. Pulisci authorization service
    this.authzService.clearUser();

    // 3. Pulisci sessionStorage (demo professional ID, profile, ecc.)
    sessionStorage.removeItem('professional_id');
    sessionStorage.removeItem('professional_profile');
    sessionStorage.removeItem('auth_state');
    sessionStorage.removeItem('auth_code_verifier');

    // 4. Logout OAuth (pulisce localStorage e reindirizza a /login)
    // redirectToAzure=false perché non vogliamo fare logout da Azure AD
    this.authService.logout(false);

    console.log('✅ [Headerbar] Logout completed');
  }
}
